import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { User, FileText, MapPin, Phone, Mail, Tag, Globe, Instagram, Facebook, Store, Upload } from 'lucide-react';
import { StepHeader, WizField, WizInput, WizSelect, WizTextarea, WizSwitch } from '../wizardUi';

const BIZ_TYPE_KEYS = ['bizType1', 'bizType2', 'bizType3', 'bizType4', 'bizType5', 'bizType6', 'bizType7', 'bizType8', 'bizType9', 'bizType10'];

function formatSize(bytes) {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

export default function Step2BusinessInfo({ wizard }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const { business, setBusinessField } = wizard;

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusinessField('infoFileName', file.name);
    setBusinessField('infoFileSize', file.size);
  }

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <StepHeader eyebrow={t('wizardIg.eyebrow', { n: 2 })} title={t('wizardIg.title1')} sub={t('wizardIg.sub1')} />

      <WizField icon={<Store size={15} />} label={t('wizardIg.labelBusinessType')}>
        <WizSelect value={business.type} onChange={(e) => setBusinessField('type', e.target.value)}>
          <option value="" disabled>{t('wizardIg.bizTypePlaceholder')}</option>
          {BIZ_TYPE_KEYS.map((key) => (
            <option key={key} value={key}>{t(`wizardIg.${key}`)}</option>
          ))}
          <option value="other">{t('wizardIg.bizTypeOther')}</option>
        </WizSelect>
        {business.type === 'other' && (
          <WizInput
            value={business.typeOther}
            onChange={(e) => setBusinessField('typeOther', e.target.value)}
            placeholder={t('wizardIg.bizTypeOtherPlaceholder')}
            className="mt-1"
          />
        )}
      </WizField>

      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
        <div className="flex flex-col gap-4 lg:gap-5">
          <WizField icon={<User size={15} />} label={t('wizardIg.labelName')}>
            <WizInput
              value={business.name}
              onChange={(e) => setBusinessField('name', e.target.value)}
              placeholder={t('wizardIg.phName')}
            />
          </WizField>

          <WizField icon={<FileText size={15} />} label={t('wizardIg.labelDesc')}>
            <WizTextarea
              rows={3}
              value={business.description}
              onChange={(e) => setBusinessField('description', e.target.value)}
              placeholder={t('wizardIg.phDesc')}
            />
          </WizField>

          <WizField icon={<MapPin size={15} />} label={t('wizardIg.labelAddress')}>
            <WizInput
              value={business.address}
              onChange={(e) => setBusinessField('address', e.target.value)}
              placeholder={t('wizardIg.phAddress')}
            />
          </WizField>
        </div>

        <div className="flex flex-col gap-4 lg:gap-5">
          <WizField icon={<Phone size={15} />} label={t('wizardIg.labelPhone')}>
            <WizInput
              type="tel"
              value={business.phone}
              onChange={(e) => setBusinessField('phone', e.target.value)}
              placeholder="+506 8888 0000"
            />
          </WizField>

          <WizField icon={<Mail size={15} />} label={t('wizardIg.labelEmail')}>
            <WizInput
              type="email"
              value={business.email}
              onChange={(e) => setBusinessField('email', e.target.value)}
              placeholder="hello@business.com"
            />
          </WizField>

          <WizField icon={<Tag size={15} />} label={t('wizardIg.labelSocial')}>
            <WizInput
              value={business.social}
              onChange={(e) => setBusinessField('social', e.target.value)}
              placeholder={t('wizardIg.phSocial')}
            />
          </WizField>

          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="flex items-center gap-2 text-[13.5px] font-medium text-white/80">
              <span className="text-white/45"><Globe size={15} /></span>
              {t('wizardIg.rowPublic')}
            </span>
            <WizSwitch
              checked={business.isPublic}
              onCheckedChange={(v) => setBusinessField('isPublic', v)}
              label={t('wizardIg.rowPublic')}
            />
          </div>

          <WizField label={t('wizardIg.labelChannels')}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-[13.5px] font-medium text-white/80">
                    <span className="text-white/45"><Instagram size={15} /></span>
                    {t('wizardIg.labelInstagram')}
                  </span>
                  <WizSwitch
                    checked={business.instagramEnabled}
                    onCheckedChange={(v) => setBusinessField('instagramEnabled', v)}
                    label={t('wizardIg.labelInstagram')}
                  />
                </div>
                {business.instagramEnabled && (
                  <WizInput
                    value={business.instagramHandle}
                    onChange={(e) => setBusinessField('instagramHandle', e.target.value)}
                    placeholder={t('wizardIg.phInstagramHandle')}
                  />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-[13.5px] font-medium text-white/80">
                    <span className="text-white/45"><Facebook size={15} /></span>
                    {t('wizardIg.labelFacebook')}
                  </span>
                  <WizSwitch
                    checked={business.facebookEnabled}
                    onCheckedChange={(v) => setBusinessField('facebookEnabled', v)}
                    label={t('wizardIg.labelFacebook')}
                  />
                </div>
                {business.facebookEnabled && (
                  <WizInput
                    value={business.facebookHandle}
                    onChange={(e) => setBusinessField('facebookHandle', e.target.value)}
                    placeholder={t('wizardIg.phFacebookHandle')}
                  />
                )}
              </div>
            </div>
          </WizField>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFile} />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center justify-center gap-3 rounded-[22px] border-[1.5px] border-dashed border-white/[0.14] px-5 py-4 text-center text-white/55 transition-colors hover:border-primary/60 hover:text-white/80"
      >
        <Upload size={19} />
        <span className="flex flex-col items-start text-left">
          <strong className="text-[13.5px] font-semibold text-foreground">{t('wizardIg.uploadBizTitle')}</strong>
          <span className="text-[12px]">{t('wizardIg.uploadBizHint')}</span>
        </span>
      </button>

      {business.infoFileName && (
        <div className="flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 lg:max-w-sm">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-destructive text-white">
            <FileText size={16} />
          </div>
          <div className="min-w-0">
            <b className="block truncate text-[13px] font-semibold">{business.infoFileName}</b>
            <span className="text-[11.5px] text-white/45">
              {formatSize(business.infoFileSize)} — {t('wizardIg.optionalLabel')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
